import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, SyntheticEvent, useCallback } from 'react';

import { IcDownloadIcon, IcLoad24Icon } from '@gem/icons';
import { IconButton, IconViewSizes } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { useFileDownloadStatePresenter } from 'APP/hooks/useFileDownloadState/useFileDownloadState';
import { useFileUploadStatePresenter } from 'APP/hooks/useFileUploadState/useFileUploadState';
import { InfiniteUploadIcon } from 'APP/main/components/InfiniteUploadIcon/InfiniteUploadIcon';
import { ProgressLoadIcon } from 'APP/main/components/ProgressLoadIcon/ProgressLoadIcon';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { getMessagePayload } from 'UTILS/message/message';

import styles from './MediaGalleryDownloadState.styles.m.css';

interface IMediaGalleryDownloadStateProps {
  className?: string;
  payloadId: string;
  message: ChatMessage;
}

export const MediaGalleryDownload: FC<IMediaGalleryDownloadStateProps> = observer((props) => {
  const { className, payloadId, message } = props;

  const payload = getMessagePayload(message, payloadId);

  const fileUploadPresenter = useFileUploadStatePresenter({
    uploadProgress: payload?.uploadProgress,
    cancelUpload: payload?.cancelUploadAndDeleteMessage,
    hasError: message.hasError,
  });

  const fileDownloadPresenter = useFileDownloadStatePresenter({
    payloadId,
    groupId: message.groupId,
    messageId: message.id,
  });

  const onCancel = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      if (fileUploadPresenter.uploadProgress !== null) {
        fileUploadPresenter.cancelUpload();
      } else {
        fileDownloadPresenter.onAbort();
      }
    },
    [fileUploadPresenter.uploadProgress]
  );

  if (fileUploadPresenter.isUploadProcess || fileDownloadPresenter.isDownloadInProcess) {
    return (
      <button className={classNames(styles.icon, className)}>
        <ProgressLoadIcon
          Icon={IcLoad24Icon}
          progress={
            fileUploadPresenter.uploadProgress || fileDownloadPresenter.downloadPercent || 0
          }
          onClick={onCancel}
        />
      </button>
    );
  }

  if (
    fileUploadPresenter.isWaitingUploadProcess ||
    fileDownloadPresenter.isWaitingDownloadProcess
  ) {
    return (
      <button className={classNames(styles.icon, className)}>
        <InfiniteUploadIcon
          Icon={IcLoad24Icon}
          onClick={onCancel}
        />
      </button>
    );
  }

  return (
    <IconButton
      className={className}
      Icon={IcDownloadIcon}
      size={IconViewSizes.Big}
      iconSize={IconViewSizes.Medium}
      theme="11"
      onClick={(): void => {
        Tasks.downloadMessageFile({
          groupId: message.groupId,
          messageId: message.id,
          payloadId,
        });
      }}
    />
  );
});
